import styles from "./ModifyUser.module.css";
import { getPageFromName } from "../../route_utils";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api_endpoints from "../../api";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import { TextField, FormControlLabel, Switch, Button, MenuItem } from "@mui/material";
import ButtonLoading from "../../widgets/ButtonLoading/ButtonLoading";
import CancelButton from "../../widgets/CancelButton/CancelButton";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";

const ModifyUser = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const userId = state?.user_id;

  const {
    setShowSuccessAlert,
    setMessageSuccessAlert,
  } = useSuccessAlert();

  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    location_id: "",
    kiosk_code: "",
    has_kiosk_code: false,
    is_active: true,
    role: "Admin",
    is_super_user: false,
  });

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setLoadingError(true);
      return;
    }

    fetchUser();
  }, [userId]);

  const fetchLocations = async (organizationId) => {
    try {
      const response = await api_endpoints.getLocations(organizationId, "", "");
      if (response.status === 200) {
        setLocations(response.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUser = async () => {
    setLoading(true);
    setLoadingError(false);

    try {
      const response = await api_endpoints.getUserById(userId);

      if (response.status === 200) {
        const user = response.data;

        setFormData({
          first_name: user.first_name || "",
          last_name: user.last_name || "",
          email: user.email || "",
          phone: user.phone || "",
          location_id: user.location_id || "",
          kiosk_code: "",
          has_kiosk_code: Boolean(user.has_kiosk_code),
          is_active: Boolean(user.is_active),
          role: user.role || (user.is_super_user ? "SuperAdmin" : "Admin"),
          is_super_user: Boolean(user.is_super_user),
        });

        if (user.organization_id) {
          await fetchLocations(user.organization_id);
        }
      } else {
        setLoadingError(true);
      }
    } catch (error) {
      console.error(error);
      setLoadingError(true);
    } finally {
      setLoading(false);
    }
  };

  const changeFormData = (key, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const canManageKioskPin = !formData.is_super_user && formData.role !== "SuperAdmin";

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        ...(canManageKioskPin ? { location_id: formData.location_id || null } : {}),
        ...(canManageKioskPin && formData.kiosk_code.trim()
          ? { kiosk_code: formData.kiosk_code.trim() }
          : {}),
        is_active: formData.is_active,
      };

      const response = await api_endpoints.updateUser(userId, payload);

      if (response.status === 200) {
        setMessageSuccessAlert("Utilisateur modifié avec succès");
        setShowSuccessAlert(true);

        navigate(getPageFromName("Voir l'utilisateur").path, {
          state: { user_id: userId },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleResetKioskPin = async () => {
    if (!userId || !window.confirm("Réinitialiser le NIP kiosk de cet utilisateur ?")) {
      return;
    }

    setSaving(true);

    try {
      const response = await api_endpoints.resetUserKioskPin(userId);

      if (response.status === 200) {
        changeFormData("kiosk_code", "");
        changeFormData("has_kiosk_code", false);
        setMessageSuccessAlert("NIP kiosk réinitialisé avec succès");
        setShowSuccessAlert(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <LoadingContainer loading={loading} error={loadingError}>
      <div className={styles.main_container}>
        <form id="modify_user_form" onSubmit={handleSubmit}>
          <div className={styles.user_details_container}>
            <div className="text-lg font-semibold">Modifier l'utilisateur</div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <TextField
                fullWidth
                required
                label="Prénom"
                value={formData.first_name}
                onChange={(e) => changeFormData("first_name", e.target.value)}
              />

              <TextField
                fullWidth
                required
                label="Nom"
                value={formData.last_name}
                onChange={(e) => changeFormData("last_name", e.target.value)}
              />

              <TextField
                fullWidth
                required
                label="Courriel"
                value={formData.email}
                onChange={(e) => changeFormData("email", e.target.value)}
              />

              <TextField
                fullWidth
                label="Téléphone"
                value={formData.phone}
                onChange={(e) => changeFormData("phone", e.target.value)}
              />

              {canManageKioskPin ? (
                <>
                  <TextField
                    select
                    fullWidth
                    label="Emplacement"
                    value={formData.location_id}
                    onChange={(e) => changeFormData("location_id", e.target.value)}
                    helperText="Obligatoire si un NIP kiosk est défini"
                  >
                    <MenuItem value="">Aucun</MenuItem>
                    {locations.map((location) => (
                      <MenuItem key={location._id} value={location._id}>
                        {location.location_name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <div className="flex space-x-2 items-start">
                    <TextField
                      fullWidth
                      label="Modifier le NIP kiosk"
                      placeholder={
                        formData.has_kiosk_code
                          ? "NIP déjà configuré"
                          : "Entrer un NIP de 4 ou 5 chiffres"
                      }
                      helperText={
                        formData.has_kiosk_code
                          ? "Laisser vide pour conserver le NIP actuel. Entrer 4 ou 5 chiffres pour le remplacer."
                          : "Entrer 4 ou 5 chiffres pour définir le NIP kiosk."
                      }
                      value={formData.kiosk_code}
                      onChange={(e) =>
                        changeFormData(
                          "kiosk_code",
                          e.target.value.replace(/\D/g, "").slice(0, 5)
                        )
                      }
                      inputProps={{
                        maxLength: 5,
                        inputMode: "numeric",
                        pattern: "^[0-9]{4,5}$",
                      }}
                    />
                    <Button
                      color="error"
                      variant="outlined"
                      disabled={saving || !formData.has_kiosk_code}
                      onClick={handleResetKioskPin}
                      sx={{ minWidth: 170, height: 56 }}
                    >
                      Réinitialiser
                    </Button>
                  </div>
                </>
              ) : null}

              <div className="flex items-center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) =>
                        changeFormData("is_active", e.target.checked)
                      }
                    />
                  }
                  label="Utilisateur actif"
                />
              </div>
            </div>
          </div>
        </form>

        <div className="flex justify-end space-x-4 mt-4">
          <CancelButton disabled={saving} label="Annuler" />
          <ButtonLoading
            variant="contained"
            type="submit"
            form="modify_user_form"
            loading={saving}
          >
            Enregistrer
          </ButtonLoading>
        </div>
      </div>
    </LoadingContainer>
  );
};

export default ModifyUser;
